import policy from './policy';
import getCookie, { COOKIE_MH_TOKEN } from './cookies';
import { decodeJWTPayload } from './utils/util';
import { INSTRUCTOR_PRODUCTS_REMOVED_KEY, SearchTermAnalyticsConstants } from './constants/anayltics.constants';

export const STORAGE_KEY = 'MHECOMM_ECOMM_UI_STATE';
const DATA_LAYER_KEY = 'MHECOMM_DATA_LAYER';
const DATA_LAYER_SIZE = 30;
const LOCAL_STORAGE_KEYS_WITH_NO_EXPIRATION_TIME = [
  SearchTermAnalyticsConstants.SEARCH_PAGE_LOCAL_STORAGE_OBJECT,
  SearchTermAnalyticsConstants.RELATED_PRODUCTS_MAPPER,
  INSTRUCTOR_PRODUCTS_REMOVED_KEY,
];

class MhecommSessionStorage {

    getStorageKey() {
        try {
            const sessionToken = getCookie(COOKIE_MH_TOKEN);
            const sessionId = sessionToken && decodeJWTPayload(sessionToken)?.session_id;
            return sessionId ? `${STORAGE_KEY}_${sessionId}` : STORAGE_KEY;
        } catch {
            return STORAGE_KEY;
        }
    }

    getState(): any {
        if (policy.acceptedCookies()) {
            try {
                return JSON.parse(sessionStorage.getItem(this.getStorageKey()) || '');
            } catch {
                return undefined;
            }
        } else {
            return undefined;
        }
    }

    saveState(state: any) {
        if (policy.acceptedCookies()) {
            if (state) {
                try {
                    sessionStorage.setItem(this.getStorageKey(), JSON.stringify(state));
                    return {
                        isSuccess: true,
                        message: 'Successfull saved to session storage.',
                        error: undefined
                    };
                } catch (error) {
                    return {
                        isSuccess: false,
                        message: 'Error trying to save to session storage.',
                        error
                    }
                }
            } else {
                return {
                    isSuccess: true,
                    message: 'No state was saved to session storage because the state was falsy.',
                    error: undefined
                }
            }
        } else {
            return {
                isSuccess: false,
                message: 'The user has declined access to cookies.',
                error: undefined
            }
        }
    }

    clearUserState(filterState: Function){
        const state = filterState(this.getState());
        sessionStorage.clear();
        this.saveState(state);
    }
}

class MhecommLocalStorage {
    get(key): any {
        if (policy.acceptedCookies()) {
            try {
                const {value, expiryTime} = JSON.parse(localStorage.getItem(key) || '')
                if (expiryTime && new Date().getTime() <= expiryTime) {
                    return value;
                }
                // bypassing the expiryTime check for this key for keys that doesn't have an expiry time
                if (LOCAL_STORAGE_KEYS_WITH_NO_EXPIRATION_TIME.includes(key) && value) {
                    return value;
                }
                return undefined;
            } catch {
                return undefined;
            }
        } else {
            return undefined;
        }
    }

    set(key: string, value: any, minutes: number|null) {
        if (policy.acceptedCookies()) {
            if (key && value) {
                try {
                    const expiryTime = minutes ? new Date().getTime() + (minutes * 60 * 1000) : undefined;
                    localStorage.setItem(key, JSON.stringify({value, expiryTime}));
                    return {
                        isSuccess: true,
                        message: 'Successfully saved to local storage.',
                        error: undefined
                    };
                } catch (error) {
                    return {
                        isSuccess: false,
                        message: 'Error trying to save to local storage.',
                        error
                    }
                }
            } else {
                return {
                    isSuccess: true,
                    message: 'No state was saved to local storage because the state was falsy.',
                    error: undefined
                }
            }
        } else {
            return {
                isSuccess: false,
                message: 'The user has declined access to cookies.',
                error: undefined
            }
        }
    }

    remove(key: string) {
        if(key) {
            try {
                localStorage.removeItem(key);
                return {
                    isSuccess: true,
                    message: 'Successfully removed key from local storage.',
                    error: undefined
                };
            } catch (error) {
                return {
                    isSuccess: false,
                    message: 'Error when trying to remove key from local storage.',
                    error
                }
            }
        } else {
            return {
                isSuccess: true,
                message: 'No state was saved to local storage because the state was falsy.',
                error: undefined
            }
        }
    }

    /**
     * @param event The data you want to push into the data layer.
     * @returns True if and only if the push was successful.
     */
    pushDataLayer(event: any): any {
        if (policy.acceptedCookies()) {
            try {
                const dataLayer: Array<any> = JSON.parse(localStorage.getItem(DATA_LAYER_KEY) || '[]');
                dataLayer.unshift(event);
                localStorage.setItem(DATA_LAYER_KEY, JSON.stringify(dataLayer.slice(0, DATA_LAYER_SIZE)));

                return true;
            } catch (e) {
                return false;
            }
        } else {
            return false;
        }
    }

    checkExpiry(key): any {
        if (policy.acceptedCookies()) {
            try {
                const {expiryTime} = JSON.parse(localStorage.getItem(key) || '')
                if (expiryTime && new Date().getTime() <= expiryTime) {
                    return false;
                }
                return true;
            } catch {
                return undefined;
            }
        } else {
            return undefined;
        }
    }
}

export const mhecommSessionStorage = new MhecommSessionStorage();

export const mhecommLocalStorage = new MhecommLocalStorage();